import { StanButton, StanModal, StanTextArea, StanTooltip } from '@/components';
import IcHelp from '@/assets/icons/ic-help-circle.svg';
import { useState } from 'react';
import { SENTRY_FEATURES, stanNotify } from '@/lib';
import { submitDispute } from '@/lib/api/income/api';
import { DisputeFileUpload } from './DisputeFileUpload';

interface DisputeCardProps {
    disputeId?: string;
    paymentId: string;
    onDisputeSubmitted: () => void;
}
const submitEvidenceTooltip = (
    <div className='p-2 text-left text-xs'>
        Learn more about the dispute process{' '}
        <a target='_blank' href='https://help.stan.store/article/49-stripe'>
            here.
        </a>
    </div>
);
export function DisputeCard({ disputeId, paymentId, onDisputeSubmitted }: DisputeCardProps) {
    const [uncategorizedText, setUncategorizedText] = useState('');
    const [customerCommunicationFileId, setCustomerCommunicationFileId] = useState<number>();
    const [submittingDisputes, setSubmittingDisputes] = useState(false);
    const [isDisputeModalOpen, setIsDisputeModalOpen] = useState(false);

    const subheadingDisputeModal = () => {
        if (!customerCommunicationFileId && !uncategorizedText) {
            return 'Are you sure you’d like to submit without <span class="font-bold">Customer Communication as <a href="https://help.stan.store/article/49-stripe-disputes" >compelling evidence</a></span> or <span class="font-bold">Additional Details</span> as evidence? <br /><br />Once submitted, you cannot edit/add more evidence.';
        }
        if (!customerCommunicationFileId) {
            return 'Are you sure you’d like to submit without any <span class="font-bold">Customer Communication as <a href="https://help.stan.store/article/49-stripe-disputes" >compelling evidence</a></span>   <br /><br />Once submitted, you cannot edit/add more evidence.';
        }
        if (!uncategorizedText) {
            return 'Are you sure you’d like to submit without <span class="font-bold">Additional Details</span> as evidence? <br /><br />Once submitted, you cannot edit/add more evidence.';
        }
        return 'Once submitted, you cannot edit/add more evidence.';
    };

    const resetDispute = () => {
        setSubmittingDisputes(false);
        setUncategorizedText('');
        setCustomerCommunicationFileId(undefined);
    };

    const handleSubmitDispute = () => {
        setIsDisputeModalOpen(false);
        setSubmittingDisputes(true);
        const payload = {
            payment_id: paymentId,
            customer_communication_file_id: customerCommunicationFileId,
            uncategorized_text: uncategorizedText,
        };
        submitDispute(payload)
            .then(() => {
                stanNotify({
                    type: 'success',
                    title: 'Success!',
                    text: 'Dispute Evidence successfully submitted.',
                    duration: 3000,
                });
                resetDispute();
                onDisputeSubmitted();
            })
            .catch(err => {
                stanNotify({
                    type: 'error',
                    title: 'Failed!',
                    text: 'Dispute Evidence did not successfully submitted.',
                    duration: 3000,
                    error: {
                        message: '[DISPUTE] failed-to-submit-dispute',
                        options: {
                            tags: { feature: SENTRY_FEATURES.DISPUTE, disputeId, paymentId },
                            context: { error: err },
                        },
                    },
                });
            })
            .finally(() => {
                setSubmittingDisputes(false);
            });
    };

    return (
        <div className='flex w-full flex-col flex-wrap gap-3 md:flex-row'>
            <div className='p-0 md:max-w-1/2'>
                <h4 className='!mb-3 flex grow items-center'>
                    <span>Submit Dispute Evidence</span>
                    <StanTooltip
                        className='d-inline-flex align-items-center ml-2'
                        trigger='click'
                        content={submitEvidenceTooltip}
                    >
                        <IcHelp />
                    </StanTooltip>
                </h4>
                <div className='text-light mb-3 max-w-lg text-xs'>
                    You only have <strong>one</strong> opportunity to provide compelling evidence to support your case.
                    Please note, that once you submit your evidence for this dispute, it will be sent directly to Stripe
                    and the customer’s corresponding bank and you will not be able to submit any additional evidence.
                </div>
                <div className='text-light mb-3 max-w-lg text-xs'>
                    Stan will automatically submit the following to Stripe on your behalf:
                    <ul>
                        <li>Transaction receipt</li>
                        <li>Proof of product delivery</li>
                        <li>Any other traceable deliveries (such as terms and conditions)</li>
                    </ul>
                </div>
                <div className='text-light mb-3 max-w-lg text-xs'>
                    We recommend that you include the following in your evidence:
                    <ul>
                        <li>Customer communications attempting to resolve the issue (eg. emails, dms)</li>
                        <li>
                            Product descriptions taken directly from your store, as well as any social media marketing
                        </li>
                        <li>Evidence of product already in use by the customer (if applicable)</li>
                        <li>Written confirmation if your customer withdrew the dispute</li>
                    </ul>
                </div>
                <div className='text-light mb-3 max-w-lg text-xs'>
                    <strong>
                        Please combine all compelling or relevant evidence into one single file (PDF, JPEG, and PNG
                        only) under 3.5 MB.
                    </strong>
                </div>
            </div>

            <div className='flex grow flex-col gap-5 p-0 md:max-w-1/2 md:flex-row'>
                <div className='h-full w-full p-0'>
                    <div className='flex h-auto max-w-full flex-col gap-2.5 md:h-full'>
                        <DisputeFileUpload onStripeFileIdChange={id => setCustomerCommunicationFileId(id)} />
                    </div>
                </div>
                <div className='w-full p-0'>
                    <div className='flex flex-col items-center gap-2.5 md:h-full'>
                        <StanTextArea
                            id='dispute-reason-text-area'
                            className='h-full'
                            placeholder='Please explain why this payment should not be disputed...'
                            value={uncategorizedText}
                            onChange={e => setUncategorizedText(e.target.value)}
                        />
                        <StanButton
                            name='submit-dispute-evidence-button'
                            className='w-full'
                            size='md'
                            onClick={() => {
                                setIsDisputeModalOpen(true);
                            }}
                            loading={submittingDisputes}
                            disabled={!customerCommunicationFileId}
                        >
                            Submit Evidence
                        </StanButton>
                    </div>
                </div>
            </div>
            <StanModal
                isOpen={isDisputeModalOpen}
                id='submit-dispute-modal'
                onClose={() => {
                    setIsDisputeModalOpen(false);
                }}
                primaryActionLabel='Submit anyway'
                secondaryActionLabel='Cancel'
                title='Are you sure?'
                onPrimaryAction={handleSubmitDispute}
                onSecondaryAction={() => {
                    setIsDisputeModalOpen(false);
                }}
            >
                <div className='text-center' dangerouslySetInnerHTML={{ __html: subheadingDisputeModal() }} />
            </StanModal>
        </div>
    );
}
